// Reusable tour steps for consistent tour experiences
import React from 'react'
import PrioritiesStrategyGoalsPrimer from './PrioritiesStrategyGoalsPrimer'

// ===============================================
// OVERVIEW & PRIMER STEPS
// ===============================================

export const createPrimerStep = (accountType = 'business') => ({
  target: 'body',
  content: <PrioritiesStrategyGoalsPrimer accountType={accountType} variant="tour" />,
  disableBeacon: true,
  placement: 'center',
  offset: 5,
  styles: {
    tooltip: {
      width: '550px'
    }
  }
})

// ===============================================
// NAVIGATION STEPS
// ===============================================

// Base navigation step factory - can customize content while keeping settings
const createNavigationStep = (target, placement = 'left', targetClickListener = null) => ({
  target,
  disableBeacon: true,
  placement,
  offset: 5,
  hideFooter: true,
  spotlightClicks: true, // Allow clicking the spotlight to advance
  disableScrolling: true,
  ...(targetClickListener && { targetClickListener })
})

export const navigationSteps = {
  openMenu: (customContent, targetClickListener) => ({
    ...createNavigationStep('#menu-link', 'bottom', targetClickListener),
    content: customContent || (
      <div>
        <h5>Open Navigation Menu</h5>
        <p>Click on this menu icon to open the navigation menu.</p>
      </div>
    )
  }),

  toPriorities: (customContent, targetClickListener) => ({
    ...createNavigationStep('[data-cy="nav-priorities"]', 'left', targetClickListener),
    content: customContent || (
      <div>
        <h5>Navigate to Priorities</h5>
        <p>Click on &quot;Priorities&quot; to navigate to the priorities page.</p>
      </div>
    )
  }),

  toStrategies: (customContent, targetClickListener) => ({
    ...createNavigationStep('[data-cy="nav-strategies"]', 'left', targetClickListener),
    content: customContent || (
      <div>
        <h5>Navigate to Strategies</h5>
        <p>Click on &quot;Strategies&quot; to navigate to the strategies page.</p>
      </div>
    )
  }),

  toGoals: (customContent, targetClickListener) => ({
    ...createNavigationStep('#goals-link', 'bottom', targetClickListener),
    content: customContent || (
      <div>
        <h5>Navigate to Goals</h5>
        <p>Click on &quot;Goals&quot; to navigate to the goals page.</p>
      </div>
    )
  }),

  toHome: (customContent) => ({
    ...createNavigationStep('#home-link', 'bottom'),
    content: customContent || (
      <div>
        <h5>Return to Dashboard</h5>
        <p>Click here to return to your dashboard.</p>
      </div>
    )
  })
}

// ===============================================
// ACTION BUTTON STEPS
// ===============================================

export const buttonSteps = {
  addButton: (entityType, customContent, targetClickListener) => ({
    target: '[data-cy="add-button"]',
    disableBeacon: true,
    placement: 'left',
    offset: 5,
    hideFooter: true,
    spotlightClicks: true, // Allow clicking the spotlight to advance
    disableScrolling: true,
    ...(targetClickListener && { targetClickListener }),
    content: customContent || (
      <div>
        <h5>Add New {entityType}</h5>
        <p>Click the + button to create a new {entityType.toLowerCase()}.</p>
      </div>
    )
  }),

  saveButton: (entityType, customContent, targetClickListener) => ({
    target: '[data-cy="modal-save-button"]',
    disableBeacon: true,
    placement: 'top',
    offset: 5,
    hideFooter: true,
    spotlightClicks: true, // Allow clicking the spotlight to advance
    disableScrolling: true,
    ...(targetClickListener && { targetClickListener }),
    content: customContent || (
      <div>
        <h5>Save Your {entityType}</h5>
        <p>Click &quot;Save&quot; to create your {entityType.toLowerCase()}.</p>
      </div>
    )
  })
}

// ===============================================
// FORM INPUT STEPS
// ===============================================

export const formSteps = {
  // Priority form steps
  priorityName: (customContent) => ({
    target: '[data-cy="priority-name-input"]',
    disableBeacon: true,
    placement: 'right',
    offset: 5,
    content: customContent || (
      <div>
        <h5>Priority Name</h5>
        <p>Enter a name for your priority. Here are some examples:</p>
        <ul>
          <li><strong>Customer Excellence</strong> - Putting customers first</li>
          <li><strong>Quality First</strong> - Never compromise on quality</li>
          <li><strong>Innovation</strong> - Continuously improve and innovate</li>
          <li><strong>Team Growth</strong> - Invest in team development</li>
        </ul>
        <p>Choose one of these or create your own!</p>
      </div>
    )
  }),

  priorityDescription: (customContent) => ({
    target: '[data-cy="priority-description-input"]',
    disableBeacon: true,
    placement: 'right',
    offset: 5,
    content: customContent || (
      <div>
        <h5>Priority Description</h5>
        <p>Add a description that explains what this priority means. For example:</p>
        <ul>
          <li>&quot;We always consider the customer impact in our decisions&quot;</li>
          <li>&quot;We deliver products and services that exceed expectations&quot;</li>
          <li>&quot;We embrace new ideas and continuously improve our processes&quot;</li>
          <li>&quot;We invest in each team member&apos;s professional growth&quot;</li>
        </ul>
      </div>
    )
  }),

  // Strategy form steps
  strategyName: (customContent) => ({
    target: '[data-cy="strategy-name-input"]',
    disableBeacon: true,
    placement: 'right',
    offset: 5,
    content: customContent || (
      <div>
        <h5>Strategy Name</h5>
        <p>Enter a name for your strategy. Here are some examples:</p>
        <ul>
          <li><strong>Customer Feedback Loop</strong> - Regular customer surveys and feedback</li>
          <li><strong>Code Review Process</strong> - All code must be reviewed for quality</li>
          <li><strong>Innovation Time</strong> - 20% time for exploring new ideas</li>
          <li><strong>Daily Stand-ups</strong> - Regular team communication</li>
        </ul>
        <p>Choose one that aligns with your priorities!</p>
      </div>
    )
  }),

  strategyDescription: (customContent) => ({
    target: '[data-cy="strategy-description-input"]',
    disableBeacon: true,
    placement: 'right',
    offset: 5,
    content: customContent || (
      <div>
        <h5>Strategy Description</h5>
        <p>Describe how this strategy helps achieve your priorities. For example:</p>
        <ul>
          <li>&quot;We survey customers monthly and act on their feedback to improve our service&quot;</li>
          <li>&quot;Every pull request requires at least one peer review before merging&quot;</li>
          <li>&quot;Team members spend Fridays exploring new technologies and approaches&quot;</li>
          <li>&quot;We meet daily to share progress, blockers, and align on priorities&quot;</li>
        </ul>
      </div>
    )
  }),

  // Goal form steps
  goalName: (customContent) => ({
    target: '[data-cy="goal-name-input"]',
    disableBeacon: true,
    placement: 'right',
    offset: 5,
    content: customContent || (
      <div>
        <h5>Goal Name</h5>
        <p>Enter a specific, measurable goal. Here are some examples:</p>
        <ul>
          <li><strong>Customer Satisfaction 95%</strong> - Achieve 95% customer satisfaction by Q4</li>
          <li><strong>Zero Critical Bugs</strong> - Maintain zero critical bugs in production</li>
          <li><strong>Innovation Projects</strong> - Complete 3 innovation projects this quarter</li>
          <li><strong>Team Efficiency</strong> - Reduce average sprint completion time by 20%</li>
        </ul>
        <p>Make it specific and measurable!</p>
      </div>
    )
  }),

  goalDescription: (customContent) => ({
    target: '[data-cy="goal-description-input"]',
    disableBeacon: true,
    placement: 'right',
    offset: 5,
    content: customContent || (
      <div>
        <h5>Goal Description</h5>
        <p>Describe how you&apos;ll measure and achieve this goal. Include timelines and success criteria:</p>
        <ul>
          <li>&quot;Survey customers monthly, aiming for 95% satisfaction scores by December 31st&quot;</li>
          <li>&quot;Implement automated testing and monitoring to catch bugs before production&quot;</li>
          <li>&quot;Allocate team time for 3 experimental projects, evaluated monthly&quot;</li>
        </ul>
      </div>
    )
  })
}

// ===============================================
// CONTEXTUAL/CELEBRATION STEPS
// ===============================================

export const contextSteps = {
  // Success celebration steps
  prioritySuccess: (customContent) => ({
    target: 'body',
    disableBeacon: true,
    placement: 'center',
    offset: 5,
    content: customContent || (
      <div>
        <h4>Excellent! 🌟</h4>
        <p>You&apos;ve created your first priority! Priorities are the foundation that guide all decisions.</p>
        <p>You can add more priorities if you&apos;d like, or we can move on to strategies.</p>
      </div>
    )
  }),

  strategySuccess: (customContent) => ({
    target: 'body',
    disableBeacon: true,
    placement: 'center',
    offset: 5,
    content: customContent || (
      <div>
        <h4>Fantastic! 🚀</h4>
        <p>You&apos;ve created your first strategy! Strategies translate your priorities into specific approaches.</p>
        <p>You can add more strategies or move on to creating goals.</p>
      </div>
    )
  }),

  goalSuccess: (customContent) => ({
    target: 'body',
    disableBeacon: true,
    placement: 'center',
    offset: 5,
    content: customContent || (
      <div>
        <h4>Outstanding! 🎯</h4>
        <p>You&apos;ve created your first goal! Goals help you measure progress and success.</p>
        <p>Consider adding more goals to track different aspects of your work.</p>
      </div>
    )
  }),

  // Understanding/explanation steps
  understandingPriorities: (customContent) => ({
    target: 'body',
    disableBeacon: true,
    placement: 'center',
    offset: 5,
    content: customContent || (
      <div>
        <h4>Understanding Priorities 📌</h4>
        <p>
          These are your organization&apos;s guiding principles. They represent what matters most 
          and should influence every decision made by the team.
        </p>
        <p>Take a moment to review and understand each priority.</p>
      </div>
    )
  }),

  understandingStrategies: (customContent) => ({
    target: 'body',
    disableBeacon: true,
    placement: 'center',
    offset: 5,
    content: customContent || (
      <div>
        <h4>Understanding Strategies 🎯</h4>
        <p>
          Strategies are the specific approaches your team uses to achieve priorities. 
          They translate high-level principles into actionable practices.
        </p>
        <p>Review how your team approaches each priority.</p>
      </div>
    )
  }),

  understandingGoals: (customContent) => ({
    target: 'body',
    disableBeacon: true,
    placement: 'center',
    offset: 5,
    content: customContent || (
      <div>
        <h4>Understanding Goals 🎯</h4>
        <p>
          Goals are measurable outcomes that help track progress. They should be specific, 
          time-bound, and directly support your team&apos;s strategies.
        </p>
        <p>Each goal should have clear success criteria.</p>
      </div>
    )
  })
}

// ===============================================
// COMPLETE SEQUENCES (Common Workflows)
// ===============================================

export const sequences = {
  // Complete priority creation sequence
  createPriority: (targetClickListener) => [
    { ...navigationSteps.openMenu(null, targetClickListener), resumePoint: true, resumePointName: 'Priority creation' },
    navigationSteps.toPriorities(null, targetClickListener),
    buttonSteps.addButton('Priority', null, targetClickListener),
    formSteps.priorityName(),
    formSteps.priorityDescription(),
    buttonSteps.saveButton('Priority', null, targetClickListener),
    contextSteps.prioritySuccess()
  ],

  // Complete strategy creation sequence
  createStrategy: (isTeamLead = false, targetClickListener) => {
    const strategyNameContent = isTeamLead ? (
      <div>
        <h5>Strategy Name</h5>
        <p>Give your strategy a clear, action-oriented name. Team strategy examples:</p>
        <ul>
          <li><strong>Weekly Team Retrospectives</strong> - Regular improvement discussions</li>
          <li><strong>Pair Programming Sessions</strong> - Knowledge sharing practice</li>
          <li><strong>Customer Support Rotation</strong> - Everyone understands customer needs</li>
          <li><strong>Documentation Standards</strong> - Clear knowledge transfer</li>
        </ul>
      </div>
    ) : null

    return [
      { ...navigationSteps.openMenu(null, targetClickListener), resumePoint: true, resumePointName: 'Strategy creation' },
      navigationSteps.toStrategies(null, targetClickListener),
      buttonSteps.addButton('Strategy', null, targetClickListener),
      formSteps.strategyName(strategyNameContent),
      formSteps.strategyDescription(),
      buttonSteps.saveButton('Strategy', null, targetClickListener),
      contextSteps.strategySuccess()
    ]
  },

  // Complete goal creation sequence
  createGoal: (roleSpecific = null, targetClickListener) => {
    const goalNameContent = roleSpecific === 'team_lead' ? (
      <div>
        <h5>Goal Name</h5>
        <p>Set measurable goals for your team&apos;s success. Examples:</p>
        <ul>
          <li><strong>Team Velocity +20%</strong> - Improve team productivity</li>
          <li><strong>100% Strategy Adoption</strong> - Ensure all team members follow strategies</li>
          <li><strong>Team Satisfaction 4.5/5</strong> - Maintain high team morale</li>
          <li><strong>Zero Missed Deadlines</strong> - Reliable delivery</li>
        </ul>
      </div>
    ) : roleSpecific === 'team_member' ? (
      <div>
        <h5>Goal Name</h5>
        <p>Create a specific, measurable goal for yourself. Examples:</p>
        <ul>
          <li><strong>Complete 5 Code Reviews Weekly</strong> - Support quality strategy</li>
          <li><strong>Reduce Bug Count by 50%</strong> - Improve personal code quality</li>
          <li><strong>Learn React Testing</strong> - Skill development goal</li>
          <li><strong>Document 3 Processes</strong> - Knowledge sharing goal</li>
        </ul>
      </div>
    ) : null

    return [
      { ...navigationSteps.toGoals(null, targetClickListener), resumePoint: true, resumePointName: 'Goal creation' },
      buttonSteps.addButton('Goal', null, targetClickListener),
      formSteps.goalName(goalNameContent),
      formSteps.goalDescription(),
      buttonSteps.saveButton('Goal', null, targetClickListener),
      contextSteps.goalSuccess()
    ]
  },

  // View sequence for team members
  viewPrioritiesAndStrategies: () => [
    navigationSteps.openMenu(),
    navigationSteps.toPriorities(),
    contextSteps.understandingPriorities(),
    navigationSteps.openMenu(),
    navigationSteps.toStrategies(),
    contextSteps.understandingStrategies()
  ]
}

// ===============================================
// EVENT HANDLER UTILITIES
// ===============================================

export const createStepHandlers = (stepIndex, advanceStep) => {
  const handlers = {}

  // Map step indices to handler functions
  const stepMap = {
    menuClick: [1, 5, 8],  // Example indices where menu is clicked
    prioritiesClick: [2],
    strategiesClick: [6],
    goalsClick: [12],
    addButtonClick: [3, 7, 13],
    saveButtonClick: [4, 10, 16]
  }

  if (stepMap.menuClick.includes(stepIndex)) {
    handlers.handleMenuClick = () => setTimeout(advanceStep, 100)
  }
  if (stepMap.prioritiesClick.includes(stepIndex)) {
    handlers.handlePrioritiesClick = () => setTimeout(advanceStep, 100)
  }
  if (stepMap.strategiesClick.includes(stepIndex)) {
    handlers.handleStrategiesClick = () => setTimeout(advanceStep, 100)
  }
  if (stepMap.goalsClick.includes(stepIndex)) {
    handlers.handleGoalsClick = () => setTimeout(advanceStep, 100)
  }
  if (stepMap.addButtonClick.includes(stepIndex)) {
    handlers.handleAddButtonClick = () => setTimeout(advanceStep, 1000)
  }
  if (stepMap.saveButtonClick.includes(stepIndex)) {
    handlers.handleSaveButtonClick = () => setTimeout(advanceStep, 500)
  }

  return handlers
}

// Helper to set up and clean up event listeners
export const setupEventListeners = (handlers) => {
  const elements = {
    menuLink: document.querySelector('#menu-link'),
    prioritiesLink: document.querySelector('[data-cy="nav-priorities"]'),
    strategiesLink: document.querySelector('[data-cy="nav-strategies"]'),
    goalsLink: document.querySelector('#goals-link'),
    addButton: document.querySelector('[data-cy="add-button"]'),
    saveButton: document.querySelector('[data-cy="modal-save-button"]'),
    homeLink: document.querySelector('#home-link')
  }

  const listeners = []

  // Add event listeners based on available handlers
  Object.entries(handlers).forEach(([handlerName, handler]) => {
    const elementMap = {
      handleMenuClick: elements.menuLink,
      handlePrioritiesClick: elements.prioritiesLink,
      handleStrategiesClick: elements.strategiesLink,
      handleGoalsClick: elements.goalsLink,
      handleAddButtonClick: elements.addButton,
      handleSaveButtonClick: elements.saveButton,
      handleHomeClick: elements.homeLink
    }

    const element = elementMap[handlerName]
    if (element && handler) {
      element.addEventListener('click', handler)
      listeners.push({ element, event: 'click', handler })
    }
  })

  // Return cleanup function
  return () => {
    listeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler)
    })
  }
}